<template>
  <div class="adw-overlay" @click.self="$emit('close')">
    <aside class="adw" role="dialog" aria-modal="true" :aria-label="`Detalle de ${cuenta.cliente}`">
      <!-- ---------- Encabezado ---------- -->
      <header class="adw-head">
        <div class="adw-head-main">
          <span class="adw-eyebrow">Detalle por cuenta</span>
          <h2 class="adw-title">{{ cuenta.cliente }}</h2>
          <div class="adw-meta">
            <span class="adw-badge" :style="{ background: cuenta.color }">
              {{ etiquetaEstado }}
            </span>
            <span v-if="vigente">
              Última evaluación: {{ vigente.fechaAplicacion || vigente.anio }}
            </span>
            <span>{{ ejecuciones.length }} encuestas ESAC</span>
          </div>
        </div>
        <button class="adw-close" aria-label="Cerrar" @click="$emit('close')">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <!-- ---------- Pestañas ---------- -->
      <nav class="adw-tabs">
        <button
          v-for="t in tabs"
          :key="t"
          class="adw-tab"
          :class="{ on: tab === t }"
          @click="tab = t"
        >
          {{ t }}
        </button>
      </nav>

      <!-- ---------- Contenido ---------- -->
      <div class="adw-body">
        <div v-if="cargando" class="adw-loading">Cargando detalle…</div>

        <template v-else>
          <!-- ===== RESUMEN ===== -->
          <section v-if="tab === 'Resumen'" class="adw-sec">
            <div class="adw-tiles">
              <div class="adw-tile">
                <span class="adw-tile-lbl">Score vigente</span>
                <span class="adw-tile-val">{{ nd(cuenta.scoreVigente) }}%</span>
              </div>
              <div class="adw-tile">
                <span class="adw-tile-lbl">NPS</span>
                <span class="adw-tile-val">{{ nd(kpis.nps, 0) }}</span>
              </div>
              <div class="adw-tile">
                <span class="adw-tile-lbl">CES</span>
                <span class="adw-tile-val">{{ nd(kpis.ces) }}</span>
              </div>
              <div class="adw-tile">
                <span class="adw-tile-lbl">CSAT</span>
                <span class="adw-tile-val">{{ nd(kpis.csat) }}</span>
                <span v-if="kpis.csatMuestra < ejecuciones.length" class="adw-tile-note">
                  {{ kpis.csatMuestra }} de {{ ejecuciones.length }}
                </span>
              </div>
            </div>

            <h3 class="adw-h3">Contactos evaluados</h3>
            <ul v-if="extra.contactos.length" class="adw-contactos">
              <li v-for="c in extra.contactos" :key="c.idcontacto || c.idContacto">
                <div class="adw-cont-main">
                  <strong>{{ c.nombre || "—" }}</strong>
                  <span class="adw-cont-puesto">{{ c.puesto || "Sin puesto" }}</span>
                </div>
                <div class="adw-cont-tags">
                  <span v-if="c.nivelinfluencia" class="adw-tag">
                    {{ c.nivelinfluencia }}
                  </span>
                  <span
                    v-if="c.evaluacioncualitativa"
                    class="adw-tag"
                    :class="tonoEval(c.evaluacioncualitativa)"
                  >
                    {{ c.evaluacioncualitativa }}
                  </span>
                </div>
              </li>
            </ul>
            <p v-else class="adw-vacio">
              Sin contactos registrados en el directorio para esta cuenta.
            </p>
          </section>

          <!-- ===== EVOLUCIÓN ===== -->
          <section v-else-if="tab === 'Evolución'" class="adw-sec">
            <p v-if="serie.length < 2" class="adw-vacio">
              La cuenta tiene medición en un solo año; no hay evolución que
              graficar.
            </p>
            <AnnualComposedChart v-else :serie="serie" :benchmark="benchmark" />
          </section>

          <!-- ===== ENCUESTAS ===== -->
          <section v-else-if="tab === 'Encuestas'" class="adw-sec">
            <table class="adw-table">
              <thead>
                <tr>
                  <th>Periodo</th>
                  <th>Contacto</th>
                  <th class="num">Score</th>
                  <th class="num">NPS</th>
                  <th class="num">CES</th>
                  <th class="num">CSAT</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="e in ejecucionesOrdenadas" :key="e.idEjecucion">
                  <td>{{ e.fechaAplicacion || e.anio || "—" }}</td>
                  <td class="adw-td-dim">{{ e.contacto || "—" }}</td>
                  <td class="num"><strong>{{ nd(e.scorePct) }}</strong></td>
                  <td class="num">{{ nd(e.nps, 0) }}</td>
                  <td class="num">{{ nd(e.ces, 0) }}</td>
                  <td class="num">{{ nd(e.csat, 0) }}</td>
                  <td>
                    <span class="adw-dot" :style="{ background: colorSem(e.semaforo) }"></span>
                    {{ e.semaforo }}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <!-- ===== ÁREAS ===== -->
          <section v-else-if="tab === 'Áreas'" class="adw-sec">
            <AreaEvolutionChart v-if="categorias.disponible" :cat="categorias" />
            <p v-else class="adw-vacio">
              Sin desglose por sección comparable para esta cuenta.
            </p>
          </section>

          <!-- ===== HALLAZGOS ===== -->
          <section v-else-if="tab === 'Hallazgos'" class="adw-sec">
            <p class="adw-nota">
              Comentarios capturados en la última evaluación. Son texto libre:
              el modelo no tiene una taxonomía de hallazgos que permita
              clasificarlos o construir un Pareto.
            </p>

            <template v-if="extra.comentarios.length || extra.respuestasAbiertas.length">
              <div v-for="c in extra.comentarios" :key="'c' + c.seccion" class="adw-hallazgo">
                <span class="adw-hall-sec">{{ c.seccion }}</span>
                <p>{{ c.comentario }}</p>
              </div>
              <div
                v-for="r in extra.respuestasAbiertas"
                :key="'r' + r.idPregunta"
                class="adw-hallazgo"
              >
                <span class="adw-hall-sec">{{ r.seccion }} · pregunta abierta</span>
                <p class="adw-hall-q">{{ r.texto }}</p>
                <p>{{ r.comentario }}</p>
              </div>
            </template>
            <p v-else class="adw-vacio">
              La última evaluación no registró comentarios abiertos.
            </p>
          </section>

          <!-- ===== INCIDENCIAS ===== -->
          <section v-else-if="tab === 'Incidencias'" class="adw-sec">
            <p class="adw-nota">
              El modelo no tiene planes de acción; se muestran las incidencias
              del portal de seguimiento asociadas a la cuenta.
            </p>

            <table v-if="incidencias.length" class="adw-table">
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Asunto</th>
                  <th>Área</th>
                  <th>Inicio</th>
                  <th>Estatus</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="i in incidencias.slice(0, 40)" :key="i.folio">
                  <td class="adw-td-dim">{{ i.folio }}</td>
                  <td class="adw-asunto" :title="i.asunto">{{ i.asunto }}</td>
                  <td class="adw-td-dim">{{ i.areaResponsable || "—" }}</td>
                  <td class="adw-td-dim">{{ i.fechaInicial || "—" }}</td>
                  <td>{{ i.estatus }}</td>
                </tr>
              </tbody>
            </table>
            <p v-else class="adw-vacio">
              Sin incidencias registradas para esta cuenta en el portal.
            </p>
          </section>
        </template>
      </div>
    </aside>
  </div>
</template>

<script>
import AnnualComposedChart from "./AnnualComposedChart.vue";
import AreaEvolutionChart from "./AreaEvolutionChart.vue";
import dashboardService from "../../services/dashboard.service";

const COLOR_SEM = {
  VERDE: "#43B234",
  AMARILLO: "#FFC24B",
  ROJO: "#FF5D73",
  SIN_DATO: "#8090B0",
};

const ETIQUETAS = {
  VERDE: "Favorable",
  AMARILLO: "En seguimiento",
  ROJO: "Crítico",
  SIN_DATO: "Sin evaluar",
};

export default {
  name: "AccountDetailDrawer",
  components: { AnnualComposedChart, AreaEvolutionChart },

  props: {
    // Fila del comparativo: { idCuenta, cliente, scoreVigente, color, semaforo }
    cuenta: { type: Object, required: true },
    // Ejecuciones de esta cuenta, ya hidratadas
    ejecuciones: { type: Array, default: () => [] },
    // Serie anual de la cuenta y referencia ONEST
    serie: { type: Array, default: () => [] },
    benchmark: { type: Array, default: null },
    // Incidencias del portal filtradas por nombre de cliente
    incidencias: { type: Array, default: () => [] },
  },

  emits: ["close"],

  data() {
    return {
      tab: "Resumen",
      tabs: [
        "Resumen",
        "Evolución",
        "Encuestas",
        "Áreas",
        "Hallazgos",
        "Incidencias",
      ],
      cargando: true,
      extra: { categoriasItems: [], contactos: [], comentarios: [], respuestasAbiertas: [] },
      categorias: { disponible: false, matriz: [], anios: [], secciones: [] },
    };
  },

  computed: {
    ejecucionesOrdenadas() {
      return this.ejecuciones
        .slice()
        .sort((a, b) => (b.anio || 0) - (a.anio || 0) || b.idEjecucion - a.idEjecucion);
    },

    vigente() {
      return this.ejecucionesOrdenadas[0] || null;
    },

    etiquetaEstado() {
      return ETIQUETAS[this.cuenta.semaforo] || this.cuenta.semaforo;
    },

    // KPIs promediados sobre todas las ejecuciones de la cuenta.
    kpis() {
      const prom = (campo) => {
        const v = this.ejecuciones.map((e) => e[campo]).filter((x) => x != null);
        if (!v.length) return null;
        return Math.round((v.reduce((a, b) => a + b, 0) / v.length) * 10) / 10;
      };
      const votos = this.ejecuciones.map((e) => e.nps).filter((v) => v != null);
      const nps = votos.length
        ? Math.round(
            ((votos.filter((v) => v >= 9).length -
              votos.filter((v) => v <= 6).length) /
              votos.length) *
              100,
          )
        : null;
      return {
        nps,
        ces: prom("ces"),
        csat: prom("csat"),
        csatMuestra: this.ejecuciones.filter((e) => e.csat != null).length,
      };
    },
  },

  async mounted() {
    document.addEventListener("keydown", this.onEsc);
    try {
      this.extra = await dashboardService.cargarDetalleCuenta(
        this.cuenta.idCuenta,
        this.vigente?.idEjecucion,
      );
      this.categorias = dashboardService.categoriasDeItems(
        this.extra.categoriasItems,
      );
    } catch (e) {
      console.error("Error cargando el detalle de la cuenta:", e);
    } finally {
      this.cargando = false;
    }
  },

  unmounted() {
    document.removeEventListener("keydown", this.onEsc);
  },

  methods: {
    onEsc(e) {
      if (e.key === "Escape") this.$emit("close");
    },

    nd(valor, decimales = 1) {
      return valor == null ? "—" : Number(valor).toFixed(decimales);
    },

    colorSem(s) {
      return COLOR_SEM[s] || COLOR_SEM.SIN_DATO;
    },

    tonoEval(v) {
      const t = String(v).toUpperCase();
      if (t.includes("PROMOTOR")) return "tag-ok";
      if (t.includes("DETRACTOR")) return "tag-bad";
      return "";
    },
  },
};
</script>

<style scoped>
.adw-overlay {
  position: fixed;
  inset: 0;
  background: rgba(4, 21, 58, 0.45);
  display: flex;
  justify-content: flex-end;
  z-index: 1050;
  animation: adw-fade 0.2s ease;
}

@keyframes adw-fade {
  from {
    opacity: 0;
  }
}

.adw {
  width: min(760px, 100%);
  height: 100%;
  background: var(--page-bg);
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-lg);
  animation: adw-slide 0.28s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes adw-slide {
  from {
    transform: translateX(40px);
    opacity: 0.4;
  }
}

/* ---------- Encabezado ---------- */
.adw-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid var(--border);
  background: var(--white);
}

.adw-eyebrow {
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 700;
  color: var(--brand-text);
}

.adw-title {
  font-size: 19px;
  font-weight: 700;
  color: var(--heading);
  margin: 2px 0 6px;
}

.adw-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 12px;
  color: var(--txt-dim);
}

.adw-badge {
  color: #fff;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 10.5px;
  font-weight: 700;
}

.adw-close {
  border: 1px solid var(--border);
  background: transparent;
  color: var(--txt-dim);
  border-radius: 10px;
  width: 34px;
  height: 34px;
  cursor: pointer;
  flex-shrink: 0;
}
.adw-close:hover {
  border-color: var(--rojo);
  color: var(--rojo);
}

/* ---------- Pestañas ---------- */
.adw-tabs {
  display: flex;
  gap: 4px;
  padding: 10px 20px 0;
  border-bottom: 1px solid var(--border);
  background: var(--white);
  overflow-x: auto;
}

.adw-tab {
  border: none;
  background: none;
  color: var(--txt-dim);
  font-size: 12.5px;
  font-weight: 600;
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  white-space: nowrap;
}
.adw-tab:hover {
  color: var(--primary);
}
.adw-tab.on {
  color: var(--brand-text);
  border-bottom-color: var(--primary);
}

/* ---------- Cuerpo ---------- */
.adw-body {
  flex: 1;
  overflow-y: auto;
  padding: 18px 20px 32px;
}

.adw-loading,
.adw-vacio {
  padding: 28px 4px;
  text-align: center;
  color: var(--txt-dim);
  font-size: 13px;
}

.adw-nota {
  font-size: 11.5px;
  color: var(--txt-faint);
  line-height: 1.5;
  margin: 0 0 14px;
  padding: 8px 11px;
  background: var(--surface);
  border-radius: 8px;
}

.adw-h3 {
  font-size: 13px;
  font-weight: 700;
  color: var(--heading);
  margin: 20px 0 10px;
}

/* ---------- Tiles ---------- */
.adw-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
}

.adw-tile {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.adw-tile-lbl {
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 700;
  color: var(--txt-faint);
}

.adw-tile-val {
  font-size: 23px;
  font-weight: 800;
  color: var(--heading);
  font-variant-numeric: tabular-nums;
}

.adw-tile-note {
  font-size: 10.5px;
  color: var(--warn);
  font-weight: 600;
}

/* ---------- Contactos ---------- */
.adw-contactos {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.adw-contactos li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 9px 12px;
  flex-wrap: wrap;
}

.adw-cont-main {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.adw-cont-main strong {
  font-size: 13px;
}

.adw-cont-puesto {
  font-size: 11.5px;
  color: var(--txt-dim);
}

.adw-cont-tags {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.adw-tag {
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  padding: 2px 9px;
  background: var(--surface-2);
  color: var(--txt-dim);
}
.adw-tag.tag-ok {
  background: rgba(67, 178, 52, 0.16);
  color: var(--verde);
}
.adw-tag.tag-bad {
  background: rgba(255, 93, 115, 0.16);
  color: var(--rojo);
}

/* ---------- Tablas ---------- */
.adw-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}

.adw-table th {
  text-align: left;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--txt-faint);
  font-weight: 700;
  padding: 7px 8px;
  border-bottom: 1px solid var(--border-2);
}

.adw-table td {
  padding: 8px;
  border-bottom: 1px solid var(--border);
}

.adw-table .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.adw-td-dim {
  color: var(--txt-dim);
  font-size: 11.5px;
}

.adw-asunto {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.adw-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 5px;
}

/* ---------- Hallazgos ---------- */
.adw-hallazgo {
  background: var(--white);
  border: 1px solid var(--border);
  border-left: 3px solid var(--primary);
  border-radius: 10px;
  padding: 10px 13px;
  margin-bottom: 8px;
}

.adw-hall-sec {
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 700;
  color: var(--brand-text);
}

.adw-hallazgo p {
  margin: 4px 0 0;
  font-size: 12.5px;
  line-height: 1.55;
  color: var(--txt-dim);
}

.adw-hall-q {
  font-style: italic;
  color: var(--txt-faint) !important;
  font-size: 11.5px !important;
}

@media (max-width: 640px) {
  .adw {
    width: 100%;
  }
}
</style>
